export type PortalMenuItem = {
  children?: PortalMenuItem[];
  icon?: unknown;
  locale?: boolean;
  name?: unknown;
  path?: string;
  [key: string]: unknown;
};

export type PortalMenuLabels = {
  administration: string;
  collaboration: string;
  delivery: string;
  deliveryEfficiency: string;
  engineeringEfficiency: string;
  feedback: string;
  insights: string;
  managementCockpit: string;
  myWorkplace: string;
  operations: string;
  organization: string;
  qualityInsights: string;
  releaseAnalytics: string;
  releaseCalendar: string;
  releaseRecord: string;
  releaseWorkbench: string;
  rollbackRecord: string;
  serviceView: string;
  workbench: string;
};

export type PortalBreadcrumbItem = {
  breadcrumbName?: unknown;
  linkPath?: string;
  path?: string;
  title?: unknown;
  [key: string]: unknown;
};

type PortalBreadcrumbRule = {
  basePath: string;
  collapseToLeaf?: boolean;
  group?: keyof PortalMenuLabels;
  label?: keyof PortalMenuLabels;
  omitBaseWhenNested?: boolean;
  section?: keyof PortalMenuLabels;
};

const V2_BREADCRUMB_RULES: PortalBreadcrumbRule[] = [
  {
    basePath: '/workplace/myTaskDetails',
    group: 'workbench',
    section: 'myWorkplace',
  },
  {
    basePath: '/workplace/departmentTaskDetails',
    group: 'workbench',
    section: 'myWorkplace',
  },
  {
    basePath: '/workplace/businessDataReport',
    group: 'workbench',
    section: 'myWorkplace',
  },
  { basePath: '/delivery/release-workbench', group: 'delivery', label: 'releaseWorkbench' },
  { basePath: '/delivery/ci/release', group: 'delivery', label: 'releaseWorkbench' },
  { basePath: '/delivery/cd/release', group: 'delivery', label: 'releaseWorkbench' },
  { basePath: '/delivery/appointment', group: 'delivery', label: 'releaseCalendar' },
  { basePath: '/delivery/service-view', group: 'delivery', label: 'serviceView' },
  { basePath: '/delivery/ci/services', group: 'delivery', label: 'serviceView' },
  { basePath: '/delivery/cd/services', group: 'delivery', label: 'serviceView' },
  { basePath: '/delivery/release-record', group: 'delivery', label: 'releaseRecord' },
  { basePath: '/delivery/ci/record', group: 'delivery', label: 'releaseRecord' },
  { basePath: '/delivery/cd/record', group: 'delivery', label: 'releaseRecord' },
  { basePath: '/delivery/gray', group: 'delivery' },
  { basePath: '/delivery/rollback', group: 'delivery', label: 'rollbackRecord' },
  { basePath: '/delivery/k8s', group: 'operations' },
  { basePath: '/dashboard/organization/duty', group: 'operations' },
  { basePath: '/dashboard/organization/weekly', group: 'workbench' },
  {
    basePath: '/dashboard/organization/team',
    collapseToLeaf: true,
    group: 'insights',
    section: 'organization',
  },
  {
    basePath: '/dashboard/dataDashboard',
    group: 'insights',
    section: 'managementCockpit',
  },
  {
    basePath: '/dashboard/efficiency/metric',
    group: 'insights',
    section: 'managementCockpit',
  },
  {
    basePath: '/dashboard/efficiency/cost',
    group: 'insights',
    section: 'managementCockpit',
  },
  {
    basePath: '/dashboard/efficiency/attendance',
    group: 'insights',
    section: 'managementCockpit',
  },
  {
    basePath: '/dashboard/efficiency/monthlyReport',
    group: 'insights',
    section: 'managementCockpit',
  },
  {
    basePath: '/dashboard/efficiency/workplace',
    group: 'insights',
    section: 'deliveryEfficiency',
  },
  {
    basePath: '/dashboard/efficiency/dashboard/requirementDashboard',
    group: 'insights',
    section: 'deliveryEfficiency',
  },
  {
    basePath: '/dashboard/efficiency/dashboard/dashboard',
    group: 'insights',
    section: 'deliveryEfficiency',
  },
  {
    basePath: '/dashboard/efficiency/dashboard',
    group: 'insights',
    section: 'deliveryEfficiency',
  },
  {
    basePath: '/dashboard/efficiency/health',
    group: 'insights',
    section: 'deliveryEfficiency',
  },
  {
    basePath: '/dashboard/efficiency/quality/testBugCharts',
    group: 'insights',
    section: 'qualityInsights',
  },
  {
    basePath: '/dashboard/efficiency/quality/onlineBugCharts',
    group: 'insights',
    section: 'qualityInsights',
  },
  {
    basePath: '/dashboard/efficiency/quality/bugProcess',
    group: 'insights',
    section: 'qualityInsights',
  },
  {
    basePath: '/dashboard/efficiency/quality/onlineBugProcess',
    group: 'insights',
    section: 'qualityInsights',
  },
  {
    basePath: '/dashboard/efficiency/quality',
    group: 'insights',
    section: 'qualityInsights',
  },
  {
    basePath: '/dashboard/efficiency/issueAndBug',
    group: 'insights',
    section: 'qualityInsights',
  },
  {
    basePath: '/dashboard/efficiency/commit',
    group: 'insights',
    section: 'engineeringEfficiency',
  },
  {
    basePath: '/dashboard/efficiency/analysis',
    group: 'insights',
    section: 'engineeringEfficiency',
  },
  {
    basePath: '/dashboard/efficiency/service/serviceStatistics',
    group: 'delivery',
    section: 'releaseAnalytics',
  },
  {
    basePath: '/dashboard/efficiency/service/serviceEmergencyStatistics',
    group: 'delivery',
    section: 'releaseAnalytics',
  },
  {
    basePath: '/dashboard/efficiency/service/serviceCntBar',
    group: 'delivery',
    section: 'releaseAnalytics',
  },
  {
    basePath: '/dashboard/efficiency/service',
    group: 'delivery',
    section: 'releaseAnalytics',
  },
  {
    basePath: '/dashboard/efficiency/model',
    group: 'insights',
    section: 'managementCockpit',
  },
  { basePath: '/dashboard/efficiency', group: 'insights' },
  { basePath: '/dashboard/issue', group: 'collaboration' },
  { basePath: '/dashboard/pool', group: 'collaboration' },
  { basePath: '/feedback' },
  { basePath: '/workplace', group: 'workbench' },
  { basePath: '/approval', group: 'workbench' },
  { basePath: '/itsm', group: 'workbench' },
  { basePath: '/subscribe', group: 'workbench' },
  { basePath: '/project', group: 'collaboration' },
  { basePath: '/rd', group: 'collaboration' },
  { basePath: '/slsDashboard', group: 'operations' },
  { basePath: '/tools', group: 'operations' },
  { basePath: '/techManagement', group: 'operations' },
  { basePath: '/cmdb', group: 'administration' },
  { basePath: '/settings', group: 'administration' },
];

const normalizePath = (path?: string): string => {
  if (!path) {
    return '';
  }

  const normalized = path.split('?')[0].replace(/\/+$/, '');
  return normalized || '/';
};

const isPathWithin = (pathname: string, basePath: string): boolean =>
  pathname === basePath || pathname.startsWith(`${basePath}/`);

const withBreadcrumbName = (item: PortalBreadcrumbItem, name: string): PortalBreadcrumbItem => ({
  ...item,
  breadcrumbName: name,
  title: name,
});

const getBreadcrumbTitle = (item: PortalBreadcrumbItem): unknown =>
  item.title ?? item.breadcrumbName;

const getBreadcrumbPath = (item: PortalBreadcrumbItem): string =>
  normalizePath(item.linkPath || item.path);

const getRetainedBreadcrumbs = (
  breadcrumbs: PortalBreadcrumbItem[],
  basePath: string,
): PortalBreadcrumbItem[] => {
  const baseIndex = breadcrumbs.findIndex((item) => getBreadcrumbPath(item) === basePath);

  if (baseIndex >= 0) {
    return breadcrumbs.slice(baseIndex);
  }

  // Hidden pages and dynamic routes do not always carry their directory node in
  // ProLayout's breadcrumb list. Keep the first descendant instead of dropping
  // the whole route tail, otherwise V2 can render only the synthetic top group.
  const descendantIndex = breadcrumbs.findIndex((item) => {
    const itemPath = getBreadcrumbPath(item);
    return itemPath !== '' && isPathWithin(itemPath, basePath);
  });

  if (descendantIndex >= 0) {
    return breadcrumbs.slice(descendantIndex);
  }

  // Some parentKeys configurations expose only a named leaf without a usable
  // path. It is still more informative than an incomplete group-only crumb.
  return breadcrumbs.length > 0 ? [breadcrumbs[breadcrumbs.length - 1]] : [];
};

/**
 * Aligns V2 breadcrumbs with the regrouped navigation while retaining the
 * original detail-page levels underneath each V2 menu entry.
 */
export const buildV2PortalBreadcrumbs = (
  pathname: string,
  breadcrumbs: PortalBreadcrumbItem[] = [],
  labels: PortalMenuLabels,
): PortalBreadcrumbItem[] => {
  const currentPath = normalizePath(pathname);
  const rule = V2_BREADCRUMB_RULES.find(({ basePath }) => isPathWithin(currentPath, basePath));

  if (!rule) {
    return breadcrumbs;
  }

  const basePath = normalizePath(rule.basePath);
  const retained = getRetainedBreadcrumbs(breadcrumbs, basePath);
  const renamed =
    rule.label && retained.length > 0
      ? [withBreadcrumbName(retained[0], labels[rule.label]), ...retained.slice(1)]
      : retained;
  const menuLevels = rule.omitBaseWhenNested && renamed.length > 1 ? renamed.slice(1) : renamed;
  const visibleLevels =
    rule.collapseToLeaf && menuLevels.length > 1 ? [menuLevels[menuLevels.length - 1]] : menuLevels;
  const section = rule.section
    ? {
        breadcrumbName: labels[rule.section],
        title: labels[rule.section],
      }
    : undefined;
  const result = rule.group
    ? [
        {
          breadcrumbName: labels[rule.group],
          title: labels[rule.group],
        },
        ...(section ? [section] : []),
        ...visibleLevels,
      ]
    : visibleLevels;

  return result.filter(
    (item, index) =>
      index === 0 || getBreadcrumbTitle(item) !== getBreadcrumbTitle(result[index - 1]),
  );
};

const findMenuItem = (menuData: PortalMenuItem[], path: string): PortalMenuItem | undefined => {
  for (const item of menuData) {
    if (item.path === path) {
      return item;
    }

    const child = findMenuItem(item.children || [], path);
    if (child) {
      return child;
    }
  }

  return undefined;
};

const cloneMenuItem = (menuData: PortalMenuItem[], path: string): PortalMenuItem | undefined => {
  const item = findMenuItem(menuData, path);
  return item ? { ...item, children: item.children?.map((child) => ({ ...child })) } : undefined;
};

const cloneMenuChildren = (menuData: PortalMenuItem[], path: string): PortalMenuItem[] =>
  cloneMenuItem(menuData, path)?.children?.map((child) => ({ ...child })) || [];

const createGroup = (
  key: keyof PortalMenuLabels,
  label: string,
  icon: unknown,
  children: Array<PortalMenuItem | undefined>,
): PortalMenuItem | undefined => {
  const visibleChildren = children.filter(Boolean) as PortalMenuItem[];
  if (visibleChildren.length === 0) {
    return undefined;
  }

  return {
    path: `/__portal/${key}`,
    name: label,
    locale: false,
    icon,
    children: visibleChildren,
  };
};

const renameMenuItem = (
  item: PortalMenuItem | undefined,
  name: string,
  path?: string,
): PortalMenuItem | undefined =>
  item
    ? {
        ...item,
        ...(path ? { path, key: path } : {}),
        name,
        locale: false,
      }
    : undefined;

/**
 * Re-groups the existing accessible routes for the V2 navigation.
 * Route paths and page components remain unchanged so historical links continue to work.
 */
export const buildV2PortalMenu = (
  menuData: PortalMenuItem[],
  labels: PortalMenuLabels,
): PortalMenuItem[] => {
  const workplace = cloneMenuItem(menuData, '/workplace');
  const rd = cloneMenuItem(menuData, '/rd');
  const dashboard = cloneMenuItem(menuData, '/dashboard');
  const delivery = cloneMenuItem(menuData, '/delivery');
  const techManagement = cloneMenuItem(menuData, '/techManagement');
  const settings = cloneMenuItem(menuData, '/settings');
  const feedbackRoot = cloneMenuItem(menuData, '/feedback');
  const feedback = renameMenuItem(feedbackRoot, labels.feedback);
  const releaseWorkbench = renameMenuItem(
    cloneMenuItem(menuData, '/delivery/cd/release') ||
      cloneMenuItem(menuData, '/delivery/ci/release'),
    labels.releaseWorkbench,
    '/delivery/release-workbench',
  );
  const weekly = cloneMenuItem(menuData, '/dashboard/organization/weekly');
  const duty = cloneMenuItem(menuData, '/dashboard/organization/duty');
  const organization = createGroup('organization', labels.organization, undefined, [
    ...cloneMenuChildren(menuData, '/dashboard/organization/team/structure'),
    cloneMenuItem(menuData, '/dashboard/organization/team/assignment'),
  ]);
  const managementCockpit = createGroup('managementCockpit', labels.managementCockpit, undefined, [
    cloneMenuItem(menuData, '/dashboard/efficiency/metric'),
    cloneMenuItem(menuData, '/dashboard/dataDashboard'),
    cloneMenuItem(menuData, '/dashboard/efficiency/cost'),
    cloneMenuItem(menuData, '/dashboard/efficiency/attendance'),
    cloneMenuItem(menuData, '/dashboard/efficiency/monthlyReport'),
    cloneMenuItem(menuData, '/dashboard/efficiency/model'),
  ]);
  const deliveryEfficiency = createGroup(
    'deliveryEfficiency',
    labels.deliveryEfficiency,
    undefined,
    [
      cloneMenuItem(menuData, '/dashboard/efficiency/workplace'),
      ...cloneMenuChildren(menuData, '/dashboard/efficiency/dashboard'),
      cloneMenuItem(menuData, '/dashboard/efficiency/health'),
    ],
  );
  const qualityInsights = createGroup('qualityInsights', labels.qualityInsights, undefined, [
    ...cloneMenuChildren(menuData, '/dashboard/efficiency/quality'),
    cloneMenuItem(menuData, '/dashboard/efficiency/issueAndBug'),
  ]);
  const engineeringEfficiency = createGroup(
    'engineeringEfficiency',
    labels.engineeringEfficiency,
    undefined,
    [
      cloneMenuItem(menuData, '/dashboard/efficiency/commit'),
      cloneMenuItem(menuData, '/dashboard/efficiency/analysis'),
    ],
  );
  const releaseAnalytics = createGroup(
    'releaseAnalytics',
    labels.releaseAnalytics,
    undefined,
    cloneMenuChildren(menuData, '/dashboard/efficiency/service'),
  );

  const groups = [
    createGroup('workbench', labels.workbench, workplace?.icon, [
      ...cloneMenuChildren(menuData, '/workplace'),
      cloneMenuItem(menuData, '/approval'),
      cloneMenuItem(menuData, '/itsm'),
      cloneMenuItem(menuData, '/subscribe'),
      weekly,
    ]),
    createGroup('collaboration', labels.collaboration, rd?.icon, [
      ...cloneMenuChildren(menuData, '/rd'),
      cloneMenuItem(menuData, '/project'),
      cloneMenuItem(menuData, '/dashboard/issue'),
      cloneMenuItem(menuData, '/dashboard/pool'),
    ]),
    createGroup('delivery', labels.delivery, delivery?.icon, [
      releaseWorkbench,
      renameMenuItem(cloneMenuItem(menuData, '/delivery/appointment'), labels.releaseCalendar),
      renameMenuItem(
        cloneMenuItem(menuData, '/delivery/cd/services') ||
          cloneMenuItem(menuData, '/delivery/ci/services'),
        labels.serviceView,
        '/delivery/service-view',
      ),
      renameMenuItem(
        cloneMenuItem(menuData, '/delivery/cd/record') ||
          cloneMenuItem(menuData, '/delivery/ci/record'),
        labels.releaseRecord,
        '/delivery/release-record',
      ),
      cloneMenuItem(menuData, '/delivery/gray'),
      renameMenuItem(cloneMenuItem(menuData, '/delivery/rollback'), labels.rollbackRecord),
      releaseAnalytics,
    ]),
    feedback,
    createGroup('operations', labels.operations, techManagement?.icon, [
      duty,
      cloneMenuItem(menuData, '/delivery/k8s'),
      cloneMenuItem(menuData, '/slsDashboard'),
      cloneMenuItem(menuData, '/tools'),
      techManagement,
    ]),
    createGroup('insights', labels.insights, dashboard?.icon, [
      managementCockpit,
      organization,
      deliveryEfficiency,
      qualityInsights,
      engineeringEfficiency,
    ]),
    createGroup('administration', labels.administration, settings?.icon, [
      cloneMenuItem(menuData, '/cmdb'),
      settings,
    ]),
  ];

  return groups.filter(Boolean) as PortalMenuItem[];
};
